import React, { useEffect, useMemo, useState, useContext } from "react";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaEye, FaPrint, FaChevronDown, FaChevronUp } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = (import.meta?.env?.VITE_API_BASE || "https://nissi-india.com").replace(/\/$/, "");

const ShutteringMaterialsReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ localStorage data
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const numericUserId = Number(userId) || 0;

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    assetSubType: "",
    size: "",
    make: "",
    fromDate: "",
    toDate: "",
  });

  const [rights, setRights] = useState({ hasRights: true, hasPrintRights: true });

  const [dropdowns, setDropdowns] = useState({
    assetSubTypes: [],
    makes: [],
  });

  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingView, setLoadingView] = useState(false);

  const [records, setRecords] = useState([]);
  const [totals, setTotals] = useState({ totalQty: 0 });

  // ✅ mapping helper
  const mapDropdown = (rows, valueKey, labelKey) => {
    if (!Array.isArray(rows)) return [];
    return rows
      .map((r) => ({
        value: r?.[valueKey] != null ? String(r[valueKey]) : "",
        label: r?.[labelKey] != null ? String(r[labelKey]) : "",
        raw: r,
      }))
      .filter((x) => x.value !== "" && x.label !== "");
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => ({
    assetSubType: filters.assetSubType,
    size: filters.size,
    make: filters.make,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    finYearId,
    finYear,
    branchId,
    branchtype: branchType,
    branchType,
    userId,
    loginuserid: userId,
  });

  const loadInit = async () => {
    if (!numericUserId) {
      toast.error("UserId missing in localStorage (userId/loginuserid)");
      return;
    }

    setLoadingInit(true);
    try {
      const q = new URLSearchParams();
      q.set("userId", String(numericUserId));
      q.set("loginuserid", String(numericUserId));
      q.set("finYearId", finYearId || "");
      q.set("finYear", finYear || "");
      q.set("branchId", branchId || "");
      q.set("branchid", branchId || "");
      q.set("branchType", branchType || "");
      q.set("branchtype", branchType || "");

      const res = await fetch(`${API_BASE}/api/shutteringReport/init?${q.toString()}`, {
        method: "GET",
        headers: commonHeaders,
      });

      const data = await res.json();
      console.log("init:", data);

      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to load init");
        return;
      }

      setRights({
        hasRights: !!data?.rights?.hasRights,
        hasPrintRights: !!data?.rights?.hasPrintRights,
      });

      // ✅ assetSubTypes fixed keys
      const assetSubTypes = mapDropdown(
        data?.dropdowns?.assetSubTypes || [],
        "fld_assetsubtypeid",
        "fld_assetsubtypename"
      );

      // ✅ makes smart mapping
      const makesRaw = data?.dropdowns?.makes || [];
      let makeValueKey = null;
      let makeLabelKey = null;

      if (makesRaw.length) {
        const keys = Object.keys(makesRaw[0] || {});
        makeValueKey = keys.find((k) => /fld_.*id$/i.test(k)) || keys[0];
        makeLabelKey =
          keys.find((k) => /make/i.test(k) || /name/i.test(k)) ||
          keys[1] ||
          keys[0];
      }

      const makes = makesRaw
        .map((r) => ({
          value: r?.[makeValueKey] != null ? String(r[makeValueKey]) : "",
          label: r?.[makeLabelKey] != null ? String(r[makeLabelKey]) : "",
          raw: r,
        }))
        .filter((x) => x.value !== "" && x.label !== "");

      setDropdowns({ assetSubTypes, makes });
    } catch (err) {
      toast.error(err?.message || "Init error");
    } finally {
      setLoadingInit(false);
    }
  };

  const handleViewClick = async () => {
    setLoadingView(true);
    try {
      const res = await fetch(`${API_BASE}/api/shutteringReport/view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...commonHeaders,
        },
        body: JSON.stringify(buildPayload()),
      });

      const data = await res.json();
      console.log("view:", data);

      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to generate report");
        return;
      }

      setRecords(Array.isArray(data?.data) ? data.data : []);
      setTotals({ totalQty: Number(data?.totals?.totalQty || 0) });

      toast.success("Report generated");
    } catch (err) {
      toast.error(err?.message || "View error");
    } finally {
      setLoadingView(false);
    }
  };

  const handlePrint = async () => {
    if (rights.hasPrintRights === false) {
      toast.error("No print rights");
      return;
    }

    try {
      const q = new URLSearchParams();
      q.set("assetSubType", filters.assetSubType || "");
      q.set("size", filters.size || "");
      q.set("make", filters.make || "");
      q.set("fromDate", filters.fromDate || "");
      q.set("toDate", filters.toDate || "");
      q.set("finYearId", finYearId || "");
      q.set("finYear", finYear || "");
      q.set("branchId", branchId || "");
      q.set("branchid", branchId || "");
      q.set("branchType", branchType || "");
      q.set("branchtype", branchType || "");
      q.set("userId", userId || "");
      q.set("loginuserid", userId || "");

      const res = await fetch(`${API_BASE}/api/shutteringReport/print?${q.toString()}`, {
        method: "GET",
        headers: commonHeaders,
      });

      if (res.ok) {
        const data = await res.json();
        if (data?.success && data?.printUrl) {
          window.open(data.printUrl, "_blank");
          return;
        }
      }

      window.open(
        `/Asset/AssetShutteringMaterial_Print?${q.toString()}`,
        "_blank"
      );
    } catch {
      const q = new URLSearchParams();
      q.set("assetSubType", filters.assetSubType || "");
      q.set("size", filters.size || "");
      q.set("make", filters.make || "");
      q.set("fromDate", filters.fromDate || "");
      q.set("toDate", filters.toDate || "");
      q.set("finYearId", finYearId || "");
      q.set("finYear", finYear || "");
      q.set("branchId", branchId || "");
      q.set("branchType", branchType || "");
      q.set("userId", userId || "");

      window.open(
        `/Asset/AssetShutteringMaterial_Print?${q.toString()}`,
        "_blank"
      );
    }
  };

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo(() => {
    if (!records.length) return [];
    return Object.keys(records[0] || {})
      .slice(0, 20)
      .map((k) => ({ key: k, label: k }));
  }, [records]);

  if (rights.hasRights === false) {
    return (
      <AssetLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <HeaderBar
            title={<span className="font-dmserif text-3xl">ASSET SHUTTERING MATERIALS REPORT</span>}
            setSkin={setSkin}
            currentSkin={skin}
          />
          <div
            className={`p-6 mt-6 rounded-2xl shadow-xl border ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : "bg-white text-black"
            }`}
          >
            <div className="text-center py-10">
              <div className="text-xl font-semibold">Access Denied</div>
              <div className="mt-2 opacity-80">You dont have rights to access this page</div>
            </div>
          </div>
        </div>
      </AssetLayoutWrapper>
    );
  }

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET SHUTTERING MATERIALS REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Asset Shuttering Materials Report
            </h1>
            <div className="text-sm opacity-80">
              {records.length ? `Rows: ${records.length}` : ""}
            </div>
          </div>

          {/* Filters */}
          <div
            className={`mb-6 rounded-lg border ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div
              className={`flex justify-between items-center p-4 cursor-pointer ${
                isDark ? "hover:bg-gray-700/50" : "hover:bg-gray-100"
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Report Filters
              </h3>
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
            </div>

            {showFilters && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Asset Sub Type
                    </label>
                    <select
                      value={filters.assetSubType}
                      onChange={(e) => handleFilterChange("assetSubType", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {dropdowns.assetSubTypes.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Make
                    </label>
                    <select
                      value={filters.make}
                      onChange={(e) => handleFilterChange("make", e.target.value)}
                      disabled={loadingInit}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {dropdowns.makes.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Size
                    </label>
                    <input
                      type="text"
                      value={filters.size}
                      onChange={(e) => handleFilterChange("size", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                      placeholder="Enter size..."
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      To Date
                    </label>
                    <input
                      type="text"
                      value={filters.toDate}
                      onChange={(e) => handleFilterChange("toDate", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                      placeholder="dd/mm/yyyy"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      From Date
                    </label>
                    <input
                      type="text"
                      value={filters.fromDate}
                      onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                      placeholder="dd/mm/yyyy"
                    />
                  </div>
                  <div />
                </div>

                <div className="flex justify-center gap-6">
                  <button
                    onClick={handleViewClick}
                    disabled={loadingView}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loadingView ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaEye />
                    {loadingView ? "Loading..." : "View"}
                  </button>

                  {rights.hasPrintRights !== false && (
                    <button
                      onClick={handlePrint}
                      className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                    >
                      <FaPrint /> Print
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {records.length === 0 ? (
              <div className={`p-12 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div
                  className={`text-xl font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-600"
                  }`}
                >
                  No Records Found!
                </div>
                <div
                  className={`text-sm ${
                    isDark ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  No shuttering materials records match the current filter criteria.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                        S.No
                      </th>
                      {columns.map((c) => (
                        <th
                          key={c.key}
                          className="px-4 py-4 text-center font-semibold border-r border-white/20"
                        >
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {records.map((r, idx) => (
                      <tr
                        key={idx}
                        className={`${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td className="px-4 py-3 text-center">{idx + 1}</td>
                        {columns.map((c) => (
                          <td key={c.key} className="px-4 py-3 text-center">
                            {r?.[c.key] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    <tr className={isDark ? "bg-gray-800" : "bg-gray-100"}>
                      <td
                        className="px-4 py-3 text-center font-semibold"
                        colSpan={1 + columns.length}
                      >
                        Total Qty (best-effort): {totals.totalQty}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default ShutteringMaterialsReport;